"use client";

import React, { createContext, useContext } from "react";
import type { Labels, LocaleCode } from "./types";

interface LabelsContextValue {
    labels: Labels;
    locale: LocaleCode;
}

const LabelsContext = createContext<LabelsContextValue | null>(null);

interface LabelsProviderProps {
    labels: Labels;
    locale: LocaleCode;
    children: React.ReactNode;
}

/**
 * Provider that makes labels and locale available to all client components.
 *
 * Not mounted directly: the root layout renders `LocaleLabelsProvider`, which picks the
 * per-locale client module under `./client/`. Each of those bundles its locale's defaults in
 * its own chunk, applies the tenant overrides and renders this provider — so the dictionary
 * never travels in the React payload. Read it with `useLabels()` / `useLocale()`.
 */
export function LabelsProvider({ labels, locale, children }: LabelsProviderProps) {
    return <LabelsContext.Provider value={{ labels, locale }}>{children}</LabelsContext.Provider>;
}

/**
 * Hook to access labels in client components.
 * Must be used within a LabelsProvider.
 *
 * Usage:
 * ```tsx
 * "use client";
 * import { useLabels } from "@/lib/labels/LabelsProvider";
 *
 * function MyComponent() {
 *     const labels = useLabels();
 *     return <h1>{labels.teamPage.tabs.overview}</h1>;
 * }
 * ```
 */
export function useLabels(): Labels {
    const context = useContext(LabelsContext);
    if (!context) {
        throw new Error("useLabels must be used within a LabelsProvider");
    }
    return context.labels;
}

/**
 * Hook to access current locale in client components.
 * Must be used within a LabelsProvider.
 *
 * Usage:
 * ```tsx
 * "use client";
 * import { useLocale } from "@/lib/labels/LabelsProvider";
 *
 * function MyComponent() {
 *     const locale = useLocale();
 *     return <span>Current language: {locale}</span>;
 * }
 * ```
 */
export function useLocale(): LocaleCode {
    const context = useContext(LabelsContext);
    if (!context) {
        throw new Error("useLocale must be used within a LabelsProvider");
    }
    return context.locale;
}
